import React from 'react';
import PropTypes from 'prop-types';
import {
  HtmlEditor,
  Inject,
  Link,
  RichTextEditorComponent,
  Toolbar,
} from '@syncfusion/ej2-react-richtexteditor';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { removeAttachments } from 'redux/actions/notificationReviewActions';

import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-html.svg';
import { ReactComponent as SoundIcon } from 'assets/icon-sound.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';
import { ReactComponent as AccessDocumentIcon } from 'assets/icon-access-document.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-zip.svg';

import { colors } from 'shared/defaultTheme';
import AttachmentBox from 'shared/components/AttachmentBox';
import {
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
} from 'shared/utils';

import { ContainerComment, ContainerConversationIcon } from './styles';

const CommentCard = ({ showTextEditor, setAnswerText, answerText, refRichTextEditor }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { attachments } = useSelector((state) => state.notificationReview);

  const items = [
    'Bold',
    'Italic',
    'Underline',
    '|',
    'Formats',
    'Alignments',
    'OrderedList',
    'UnorderedList',
    '|',
    'CreateLink',
    '|',
    'SourceCode',
    '|',
    'Undo',
    'Redo',
  ];

  const toolbarSettings = {
    items,
  };

  const onChangeAnswerText = (event) => {
    setAnswerText(event.value);
  };

  const IconFile = ({ file }) => {
    if (getIconDoc(file.type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(file.type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(file.type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(file.type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(file.type)) {
      return <SoundIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(file.type)) {
      return (
        <AccessDocumentIcon
          fill={colors.suvaGrey}
          className="control-panel__icon-type"
          width="20"
        />
      );
    }
    if (getIconPpt(file.type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(file.type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  return (
    <>
      <ContainerComment showTextEditor={showTextEditor}>
        <div className={`d-flex ${showTextEditor && 'mb-3'} `}>
          <ContainerConversationIcon>
            <ConversationIcon fill={colors.black} />
          </ContainerConversationIcon>
          <span className="ml-2">{t('notification_comment')}</span>
        </div>
        {showTextEditor && (
          <>
            <RichTextEditorComponent
              height={145}
              change={(e) => onChangeAnswerText(e)}
              toolbarSettings={toolbarSettings}
              value={answerText}
              ref={refRichTextEditor}
              saveInterval={20}
            >
              <Inject services={[Toolbar, HtmlEditor, Link]} />
            </RichTextEditorComponent>
            {attachments.length > 0 && (
              <div className="attachment-box-container">
                <div className="attachment-box">
                  {attachments.map((document, index) => (
                    <React.Fragment key={document.id}>
                      <AttachmentBox
                        name={document.name}
                        icon={<IconFile file={document} />}
                        className="mr-2 mb-2"
                        index={index}
                        remove={() => {
                          dispatch(removeAttachments({ index }));
                        }}
                      />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </ContainerComment>
    </>
  );
};

CommentCard.defaultProps = {
  answerText: '',
};

CommentCard.propTypes = {
  showTextEditor: PropTypes.bool.isRequired,
  setAnswerText: PropTypes.func.isRequired,
  answerText: PropTypes.string,
  refRichTextEditor: PropTypes.objectOf(PropTypes.shape()).isRequired,
};

export default CommentCard;
